/**
 * Engine event → query keys to invalidate (web.md §3). Freshness comes ONLY from engine events
 * (queries use `staleTime: Infinity`), so this table is what keeps every screen current.
 *
 *   | Tables in the `changed` event                    | Invalidates                                         |
 *   | messages, message_topics, message_tags           | ['list'], ['counts'], ['message', id] per listed id |
 *   |                                                  |   (['message'] = all open details when ids absent)  |
 *   | view_counts                                      | ['counts']                                          |
 *   | events, assessments, corrections                 | ['message', id], ['corrections'], ['counts']        |
 *   | folders tags topics sources rules views          | that entity list (+ ['counts'] for folders, views)  |
 *   |   connectors digests settings                    |                                                     |
 *
 * Tables the spec table does not name map to device-local keys: local_settings → ['localSettings'],
 * source_state → ['sourceStatus'], source_days → ['sourceStats']; messages also → ['sourceStats', 'overview']
 * (the Sources list numbers, recomputed on read). Other events: entitlement →
 * ['plan'] + ['localSettings'] + ['models'] (a model's `entitled` and its plan switch follow the plan); pipeline → ['pipeline']; source-error → ['sourceStatus']; limit → ['pipeline'] +
 * ['plan', 'usage']; settings also → ['pipeline'] (a changed Jev budget ends a scoring pause).
 *
 * Models (models.md §6.2, §12): `message_scores` counts as a message table (rows show who scored and who
 * is still owed); `models` → ['models'] + ['pipeline'] + ['rules'] (a record's switch changes what waits
 * and which actions on scoring are live); a `model` event (install state, download progress) →
 * ['models'] (the install queries live under it) + ['pipeline'] (a finished download ends `download` waits).
 */

import type { EngineEvent } from '@digester/core';

export type QueryKeyPart = string | number | boolean | null | Record<string, unknown>;
export type InvalidationKey = readonly QueryKeyPart[];

const MESSAGE_TABLES = new Set(['messages', 'message_topics', 'message_tags', 'message_scores']);
const HISTORY_TABLES = new Set(['events', 'assessments', 'corrections']);
const ENTITY_TABLES = new Set(['folders', 'tags', 'topics', 'sources', 'rules', 'views', 'connectors', 'digests', 'settings']);
const COUNTED_ENTITIES = new Set(['folders', 'views']);
const LOCAL_TABLES: Readonly<Record<string, string>> = {
  local_settings: 'localSettings',
  source_state: 'sourceStatus',
  source_days: 'sourceStats',
};

/** Keys to invalidate for one event, de-duplicated, in a stable order. */
export function keysForEvent(event: EngineEvent): InvalidationKey[] {
  const out: InvalidationKey[] = [];
  const seen = new Set<string>();
  const add = (key: InvalidationKey): void => {
    const id = JSON.stringify(key);
    if (seen.has(id)) return;
    seen.add(id);
    out.push(key);
  };
  const addMessages = (ids: readonly string[] | undefined): void => {
    if (ids === undefined) add(['message']);
    else for (const id of ids) add(['message', id]);
  };

  switch (event.type) {
    case 'changed': {
      const tables = new Set(event.tables);
      if ([...tables].some((t) => MESSAGE_TABLES.has(t))) {
        add(['list']);
        add(['counts']);
        // Settings → Sources numbers: sources.overview recomputes the buckets these writes made dirty.
        if (tables.has('messages')) add(['sourceStats', 'overview']);
        addMessages(event.messageIds);
      }
      if (tables.has('view_counts')) add(['counts']);
      if ([...tables].some((t) => HISTORY_TABLES.has(t))) {
        addMessages(event.messageIds);
        add(['corrections']);
        add(['counts']);
      }
      for (const table of event.tables) {
        if (ENTITY_TABLES.has(table)) {
          add([table]);
          if (COUNTED_ENTITIES.has(table)) add(['counts']);
        }
        const local = LOCAL_TABLES[table];
        if (local !== undefined) add([local]);
      }
      // A changed Jev budget resumes held scoring: the pause shown from pipeline.status() may be over.
      if (tables.has('settings')) add(['pipeline']);
      if (tables.has('models')) {
        add(['models']);
        add(['rules']);
        add(['pipeline']);
      }
      return out;
    }
    case 'entitlement':
      add(['plan']);
      add(['localSettings']);
      // Jev's "Pro" state and the models a plan change switched off or on follow the entitlement.
      add(['models']);
      return out;
    case 'pipeline':
      add(['pipeline']);
      return out;
    case 'source-error':
      add(['sourceStatus']);
      return out;
    case 'limit':
      // A daily maximum reached: pipeline.status().scoringPaused (scored_per_day) and the plan's usage changed.
      add(['pipeline']);
      add(['plan', 'usage']);
      return out;
    case 'sync':
      return out;
    case 'model':
      // An on-device model's install state / download progress on this device (models.md §6.2).
      add(['models']);
      add(['pipeline']);
      return out;
  }
}
